/* ===================== ASTROLOGER Dashboard =====================
   Role-specific Dashboard screen for astrologer accounts, rendered
   through TAB_ROLE_COMPONENTS in tabs/index.js — the seeker overview
   (OverviewTab) stays untouched for every other role.

   Practice console first: consultation + earnings stats and the live
   upcoming sessions booked WITH the astrologer (useAstrologerBookings →
   GET /astrologer/:id/bookings; skeleton while loading, empty state when
   none), alongside the spiritual cards kept from the seeker overview:
   Today's Horoscope, Today's Panchang and Track Your Spiritual Journey. */
import { BsCurrencyRupee } from 'react-icons/bs'
import { FiCalendar, FiCreditCard, FiUsers } from 'react-icons/fi'
import { appUrl } from '@astro/config'
import { useAstrologerBookings, useAstrologerEarnings, useAstrology } from '../useDashboard'

/* Earnings model: booking amounts are gross and GST-inclusive; the platform
   keeps a commission on the GST-exclusive base. Both percentages are the
   admin's (GET /astrologer/earnings reports them with the figures), so the
   constants below are only the fallback for a payload that predates them —
   they are the rates this screen hardcoded before the setting existed. */
const FALLBACK_GST_RATE = 0.18
const FALLBACK_COMMISSION_RATE = 0.30

/** Rupees with paise, e.g. 47457.626 → "₹47,457.63". */
function inr2(n) {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Gross → { gross, gst, base, commission, net } per the model above. Used only
 *  when the earnings read hasn't answered (or failed): the server's own summary
 *  is preferred, so the card can't disagree with the Earnings tab. */
function earningsBreakdown(sessions, rates) {
  const gstRate = rates ? Number(rates.gst_percent) / 100 : FALLBACK_GST_RATE
  const commissionRate = rates ? Number(rates.percent) / 100 : FALLBACK_COMMISSION_RATE
  const gross = sessions.reduce((sum, s) => sum + (s.amountValue ?? 0), 0)
  const base = gross / (1 + gstRate)
  const gst = gross - base
  const commission = base * commissionRate
  const net = base - commission
  return { gross, gst, base, commission, net }
}

/** A percentage for a caption: "30" / "27.5", never "27.500000". */
function pct(value) {
  const n = Number(value) || 0
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)))
}

export default function AstrologerOverviewTab({ onSwitch, data }) {
  const { bookings: sessions, hasBookings, isLoading: sessionsLoading } = useAstrologerBookings()
  const upcoming = sessions.filter((s) => s.join)
  // Server-costed totals (GST + the admin's commission) when the earnings read
  // answers; the local split over the booking list otherwise.
  const { summary, rates } = useAstrologerEarnings()
  const earnings = summary
    ? { gross: summary.gross, gst: summary.gst, base: summary.taxable, commission: summary.commission, net: summary.net }
    : earningsBreakdown(sessions, rates)
  // A zero rate covers both "switched off for everyone" and "this astrologer is
  // exempt" (the house astrologer's own switch) — either way nothing is kept.
  const commissionCaption = rates && (!rates.is_active || Number(rates.percent) === 0)
    ? 'No platform commission'
    : `After ${pct(rates ? rates.percent : FALLBACK_COMMISSION_RATE * 100)}% commission (on base)`

  /* This card lists confirmed bookings only, soonest first. The API returns
     every status in payload order, so both the filter and the ordering are
     derived here rather than trusted from the response — and the sort keys off
     the scheduled timestamp, so two sessions on the same day stay in
     time-of-day order. Undated rows sink to the bottom. */
  const confirmedSessions = sessions
    .filter((s) => String(s.status).toLowerCase().includes('confirm'))
    .sort((a, b) => (a.at ?? Infinity) - (b.at ?? Infinity))

  // Live astrology (VedAstro) with graceful fallback — same derivations
  // as the seeker overview so the kept cards behave identically.
  const astro = useAstrology()
  const horo = astro?.hasHoroscope ? astro.horoscope : null
  const moonSign = astro?.moonSign || 'Virgo'
  const moonGlyph = astro?.moonGlyph || '♍'
  const horoExcerpt = horo ? `${horo.text.split('. ')[0]}.` : 'A productive day for planning and taking practical steps.'
  const pan = astro?.panchang
  const panNak = pan ? String(pan.nakshatra).split(' - ')[0] : 'Uttara Ashadha'

  return (
    <div className="space-y-8">
      {/* ── Practice console strip — the astrologer's "on air" bar ── */}
      <div className="relative overflow-hidden rounded-2xl bg-[#3D0A0A] text-white p-5 shadow-lg shadow-[#3D0A0A]/15">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#D48806]/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-12 left-1/3 w-44 h-44 bg-red-900/40 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-[8px] uppercase font-bold tracking-[0.3em] text-[#D4A017]">Astrologer Console</p>
            <h2 className="text-lg font-royal text-[#FBF3E4] mt-1">Your practice at a glance</h2>
            <p className="text-[10px] text-white/50 mt-0.5">Seekers are waiting — review today&rsquo;s sessions and keep your streak alive.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="flex items-center gap-1.5 border border-white/15 bg-white/5 text-[9px] font-bold uppercase tracking-wider px-3 py-2 rounded-full">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Online
            </span>
            <button onClick={() => onSwitch('appoinments')} className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[9px] uppercase tracking-[0.15em] px-4 py-2 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all">Appoinments</button>
            <button onClick={() => onSwitch('earnings')} className="border border-white/15 hover:border-[#D4A017]/60 text-[#FBF3E4] hover:text-[#D4A017] font-semibold text-[9px] uppercase tracking-[0.15em] px-4 py-2 rounded-xl transition-all">Earnings</button>
          </div>
        </div>
      </div>

      {/* ── Practice stats — consultations + earnings breakdown ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard tone="#3D0A0A" label="Upcoming Consultations" value={String(upcoming.length)} caption="Next 7 days" onClick={() => onSwitch('appoinments')}
          icon={<FiCalendar size={20} />} />
        <StatCard tone="#D48806" label="Total Consultations" value={String(sessions.length)} caption="All time" onClick={() => onSwitch('appoinments')}
          icon={<FiUsers size={20} />} />
        <StatCard tone="#3D0A0A" label="Total Earnings" value={inr2(earnings.gross)} caption="Gross (incl. GST)" onClick={() => onSwitch('earnings')}
          icon={<FiCreditCard size={20} />} />
        <StatCard tone="#D48806" label="Your Net Earnings" value={inr2(earnings.net)} caption={commissionCaption} onClick={() => onSwitch('earnings')}
          sub={[['GST', inr2(earnings.gst)], ['Base', inr2(earnings.base)], ['Commission', inr2(earnings.commission)]]}
          icon={<BsCurrencyRupee size={20} />} />
      </div>

      {/* ── Session console + the kept spiritual cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 p-3 bg-stone-50 rounded-2xl items-start">
        {/* Upcoming sessions booked with this astrologer */}
        <div className="md:col-span-9 bg-white p-5 border border-stone-100 shadow-sm rounded-2xl w-full">
          <div className="flex flex-wrap justify-between items-center gap-x-4 gap-y-2 mb-5">
            <h3 className="text-lg font-bold text-stone-900">Upcoming Sessions</h3>
            <button onClick={() => onSwitch('appoinments')} className="text-xs font-bold text-[#8B4513] uppercase tracking-wider hover:underline">View All</button>
          </div>
          {sessionsLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <SessionSkeletonRow key={i} />
              ))}
            </div>
          ) : !confirmedSessions.length ? (
            <div className="py-10 text-center">
              <div className="text-4xl mb-3 opacity-40">🗓️</div>
              <p className="text-base font-semibold text-astroDark mb-1">
                {hasBookings ? 'No confirmed sessions' : 'No sessions booked yet'}
              </p>
              <p className="text-sm text-stone-400">
                {hasBookings
                  ? 'Bookings appear here once they are confirmed.'
                  : 'Sessions seekers book with you will appear here.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {confirmedSessions.map((s, i) => (
                <div key={s.key} className="group border border-stone-100 rounded-xl p-3 hover:shadow-md hover:border-[#D4A017]/40 transition-all">
                  <div className="flex gap-3 items-start">
                    {/* Highlight the next session up — its position in this
                        sorted list, not the payload's first row. */}
                    <div className={`text-white w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 shadow-lg ${i === 0 ? 'bg-[#3D0A0A] shadow-[#3D0A0A]/20' : 'bg-[#D48806] shadow-amber-600/20'}`}>
                      <span className="text-base font-bold leading-none">{s.day}</span>
                      <span className="text-[8px] uppercase tracking-widest opacity-80 mt-0.5">{s.mon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          {/* Lead with the customer — same idiom as the astrologer
                              view of the Consultations tab's booking cards. */}
                          <p className="text-sm font-bold text-stone-900 leading-tight truncate">{s.client || s.person || 'Seeker'}</p>
                          <p className="text-[11px] text-stone-500 font-medium mt-0.5 truncate">
                            {[s.clientMobile, s.clientEmail].filter(Boolean).join(' · ') || 'Contact details unavailable'}
                          </p>
                          <p className="text-[11px] text-stone-500 mt-0.5 truncate">{s.title} · <span className="text-[#A67C00]">{s.code}</span></p>
                        </div>
                        <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shrink-0 ${s.cls}`}>{s.status}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[10px] text-stone-400 uppercase tracking-wide font-bold">
                        {s.time ? <span>🕐 {s.time}</span> : null}
                        {s.dur ? <span>⏳ {s.dur}</span> : null}
                        {s.mode ? <span>{s.mode}</span> : null}
                        {s.amount ? <span className="text-[#A67C00]">{s.amount}</span> : null}
                      </div>
                    </div>
                  </div>
                  {s.id != null && (
                    <div className="flex mt-3 pt-3 border-t border-stone-100">
                      <a
                        href={appUrl('jyotish', `/astrologer/appointments/${s.id}`)}
                        className="border border-stone-200 px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-50"
                      >
                        View Details
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-3 space-y-3">
          {/* Today's Horoscope — kept from the seeker overview */}
          <div className="bg-white p-3 border border-stone-100 shadow-sm rounded-2xl flex flex-col">
            <div className="flex flex-wrap justify-between items-center gap-x-3 gap-y-1.5 mb-3">
              <h3 className="text-[10px] font-bold text-stone-800 uppercase tracking-wide">Today's Horoscope</h3>
              <span className="text-[8px] font-bold bg-stone-50 text-stone-500 px-1.5 py-0.5 rounded">{moonSign} {moonGlyph}</span>
            </div>
            <div className="flex flex-col items-center text-center mb-3">
              <div className="w-24 h-24 border-2 border-stone-100 rounded-full flex items-center justify-center mb-2 text-4xl text-cosmicRed bg-amber-50/40">{moonGlyph}</div>
              <p className="text-stone-500 text-[9px] leading-tight px-1 italic">&ldquo;{horoExcerpt}&rdquo;</p>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <div className="bg-stone-50 p-1 rounded-lg text-center border border-stone-100">
                <p className="text-[7px] text-stone-400 uppercase font-bold">Lucky No.</p>
                <p className="text-[10px] font-black text-amber-900">{horo?.luckyNum ?? '6'}</p>
              </div>
              <div className="bg-stone-50 p-1 rounded-lg text-center border border-stone-100">
                <p className="text-[7px] text-stone-400 uppercase font-bold">Color</p>
                <p className="text-[10px] font-black text-green-700">{horo?.luckyColor ?? 'Green'}</p>
              </div>
              <div className="bg-stone-50 p-1 rounded-lg text-center border border-stone-100">
                <p className="text-[7px] text-stone-400 uppercase font-bold">Mood</p>
                <p className="text-[10px] font-black text-amber-900">{horo?.luckyMood ?? 'Focused'}</p>
              </div>
            </div>
            <button onClick={() => onSwitch('horoscope')} className="w-full bg-amber-600 text-white py-1.5 rounded-lg font-bold text-[8px] uppercase tracking-wider hover:bg-amber-700 transition-all mb-4 mt-6">Read Full Horoscope</button>
          </div>

          {/* Today's Panchang + Track Your Spiritual Journey — kept from the seeker overview */}
          <div className="space-y-3 h-full">
            <div className="bg-white p-4 border border-stone-100 shadow-sm rounded-2xl w-full">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-[11px] font-bold text-stone-700">Today's Panchang</h3>
                <span className="text-sm">🗓️</span>
              </div>
              <h2 className="text-sm font-bold text-red-900 mb-1">Tithi: {pan?.tithi?.Name ?? 'Ekadashi'}</h2>
              <p className="text-[9px] text-stone-500 mb-4 leading-tight">Auspicious day for spiritual practices, fasting, and meditation. Good time to start new learning and financial planning.</p>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="bg-stone-50 border border-stone-100 p-2 rounded-lg text-left">
                  <p className="text-[7px] text-stone-500 uppercase font-bold tracking-wider">Nakshatra</p>
                  <p className="text-[11px] text-amber-700 font-black">{panNak}</p>
                </div>
                <div className="bg-stone-50 border border-stone-100 p-2 rounded-lg text-left">
                  <p className="text-[7px] text-stone-500 uppercase font-bold tracking-wider">Direction</p>
                  <p className="text-[11px] text-amber-700 font-black">{pan?.dishaShool ?? 'West'}</p>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 mb-4">
                <div className="col-span-8 bg-stone-50 border border-stone-100 p-2 rounded-lg text-left">
                  <p className="text-[7px] text-stone-500 uppercase font-bold tracking-wider">Rahukal</p>
                  <p className="text-[10px] text-amber-700 font-black">{pan?.rahuKalam ?? '09:00 - 10:30'}</p>
                </div>
                <div className="col-span-4 bg-stone-50 border border-stone-100 p-2 rounded-lg text-left">
                  <p className="text-[7px] text-stone-500 uppercase font-bold tracking-wider">Yog</p>
                  <p className="text-[11px] text-amber-700 font-black">{pan?.yoga?.Name ?? 'Shukla'}</p>
                </div>
              </div>
              <button className="w-full border border-stone-200 py-1.5 rounded-md text-[8px] font-bold text-stone-700 hover:bg-stone-50 uppercase tracking-widest">View Full Panchang</button>
            </div>
            <div className="bg-white p-4 border border-stone-100 shadow-sm rounded-2xl flex items-center justify-between">
              <div>
                <h3 className="text-[11px] font-bold">Track Your Spiritual Journey</h3>
                <p className="text-[9px] text-stone-400 mt-0.5">Complete your daily activities.</p>
              </div>
              <div className="w-10 h-10 rounded-full border-4 border-amber-600 flex items-center justify-center font-bold text-[9px] shrink-0">72%</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* Pulsing placeholder matching one session row — date block, text lines
   and a status pill — shown while the bookings read loads. */
function SessionSkeletonRow() {
  return (
    <div className="border border-stone-100 rounded-xl p-3 animate-pulse">
      <div className="flex gap-3 items-start">
        <div className="w-14 h-14 rounded-xl bg-stone-100 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-2 flex-1 min-w-0">
              <div className="h-3.5 w-36 bg-stone-100 rounded-full" />
              <div className="h-2.5 w-52 bg-stone-100 rounded-full" />
              <div className="h-2.5 w-44 bg-stone-100 rounded-full" />
            </div>
            <div className="h-5 w-16 bg-stone-100 rounded-full shrink-0" />
          </div>
          <div className="flex gap-3 mt-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-2.5 w-12 bg-stone-100 rounded-full" />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/* Stat card on the design system's .stat-card surface (rounded, hover
   lift, top accent bar driven by --accent). Vertical layout so all four
   cards align whatever their content: icon+label row, value, caption
   (the tap target when a destination tab exists), and an optional `sub`
   breakdown — [label, value] pairs rendered as mini chips, pinned to the
   card's bottom edge. `icon` is a rendered react-icons element. */
function StatCard({ tone, label, value, caption, sub, onClick, icon }) {
  return (
    <div className="stat-card h-full flex flex-col" style={{ '--accent': tone }}>
      <div className="flex items-center gap-3">
        <div className="stat-icon shrink-0 text-white" style={{ background: tone }}>
          {icon}
        </div>
        <p className="text-stone-500 text-[11px] font-bold uppercase tracking-wider leading-snug">{label}</p>
      </div>
      <h3 className="text-[22px] font-bold text-stone-900 mt-3 leading-tight break-words">{value}</h3>
      {onClick ? (
        <button onClick={onClick} className="mt-1 w-max text-left text-xs font-semibold text-[#A67C00] hover:underline">{caption}</button>
      ) : (
        <span className="mt-1 text-xs font-semibold text-[#A67C00]">{caption}</span>
      )}
      {sub ? (
        <div className="mt-auto pt-3 grid grid-cols-3 gap-1.5">
          {sub.map(([k, v]) => (
            <div key={k} className="bg-stone-50 border border-stone-100 rounded-lg p-1 text-center min-w-0" title={`${k}: ${v}`}>
              <p className="text-[7px] text-stone-400 uppercase font-bold tracking-wide truncate">{k}</p>
              <p className="text-[9px] font-black text-amber-900 truncate">{v}</p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
